import type { Answers, BriefingExport, BriefingMeta, Section } from "../types";
import { countRequiredAnswered, countRequiredTotal, isAnswered, isQuestionApplicable } from "./questions";
import { buildSchemaBlock, buildSelections } from "./selections";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function countTotalQuestions(sections: Section[], answers: Answers): number {
  return sections.reduce(
    (sum, section) =>
      sum + section.questions.filter((question) => isQuestionApplicable(question, answers)).length,
    0
  );
}

export function countAnsweredQuestions(sections: Section[], answers: Answers): number {
  let count = 0;
  for (const section of sections) {
    for (const question of section.questions) {
      if (!isQuestionApplicable(question, answers)) continue;
      if (isAnswered(answers[question.id])) count += 1;
    }
  }
  return count;
}

export function buildExportPayload(
  sections: Section[],
  meta: BriefingMeta,
  answers: Answers,
  briefingId: string
): BriefingExport {
  // Emit EVERY question id, null when unanswered — an export with only the
  // answered keys has no stable schema, and "why is field X missing?" is
  // indistinguishable from "field X doesn't exist".
  const fullAnswers: BriefingExport["answers"] = {};
  for (const section of sections) {
    for (const question of section.questions) {
      const value = answers[question.id];
      fullAnswers[question.id] = isAnswered(value) ? (value as Exclude<typeof value, undefined>) : null;
    }
  }
  return {
    schemaVersion: 1,
    briefing: briefingId,
    exportedAt: new Date().toISOString(),
    meta,
    schema: buildSchemaBlock(sections),
    selections: buildSelections(sections, answers),
    answers: fullAnswers,
    // Same mandatory-only model as the visible progress bar — one story.
    progress: {
      answered: countRequiredAnswered(sections, answers),
      total: countRequiredTotal(sections, answers),
    },
  };
}

export function briefingFilename(meta: BriefingMeta, dateStamp: string, prefix: string): string {
  const companySlug = slugify(meta.company);
  return companySlug ? `${prefix}_${companySlug}_${dateStamp}.json` : `${prefix}_${dateStamp}.json`;
}

export function downloadJson(payload: BriefingExport, filename: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatAnswer(question: Section["questions"][number], value: Answers[string]): string {
  if (!isAnswered(value)) return "–";
  // Option values are ids ("sidebar_labeled") — humans forward this text, so
  // translate them back to the labels shown in the form.
  if (question.options) {
    const labelByValue = new Map(question.options.map((option) => [option.value, option.label]));
    if (Array.isArray(value)) {
      return value.map((entry) => labelByValue.get(entry) ?? entry).join(", ");
    }
    return labelByValue.get(String(value)) ?? String(value);
  }
  return Array.isArray(value) ? value.join(", ") : String(value);
}

export function answersAsPlainText(
  sections: Section[],
  meta: BriefingMeta,
  answers: Answers,
  title: string
): string {
  const lines: string[] = [];
  lines.push(`${title} — ${meta.company || "(kein Unternehmen angegeben)"}`);
  lines.push(`Bericht: ${meta.reportName || "–"}`);
  lines.push("");
  for (const section of sections) {
    lines.push(`## ${section.index} ${section.title}`);
    for (const question of section.questions) {
      // List every applicable question — unanswered shows as "–", so
      // completeness is visible instead of silently omitted.
      if (!isQuestionApplicable(question, answers)) continue;
      lines.push(`${question.label}: ${formatAnswer(question, answers[question.id])}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
